/**
 * search.js — the slide-in search drawer with live product results.
 *
 * Mirrors cart.js deliberately (module-scope lastTrigger, getFocusable/trapFocus/
 * closeOnEsc, delegated init, a window.Gritt* handle) so there is one drawer
 * pattern in this theme rather than two.
 *
 * Results come from Shopify's predictive-search route rendered through a
 * section, not the .json endpoint — see sections/predictive-search.liquid for
 * why (short version: the JSON always includes prices, and this is a catalogue
 * store).
 *
 * NOTE: no ESM import from theme.js. theme.liquid loads it via a versioned CDN
 * URL, so a relative './theme.js' specifier is a different module URL and the
 * browser would instantiate it a second time, double-binding every delegated
 * listener in it. The window.GrittNav handle is the handoff instead.
 */

const DEBOUNCE_MS = 300;
const ERROR_COOLDOWN_MS = 2000;
const CACHE_MAX = 20;

/** @type {HTMLElement|null} */
let lastTrigger = null;
/** @type {AbortController|null} */
let controller = null;
/** @type {number|undefined} */
let debounceTimer;
let cooldownUntil = 0;
/** @type {Map<string,string>} */
const cache = new Map();

const getDrawer = () => document.querySelector('[data-search-drawer]');
const getOverlay = () => document.querySelector('[data-search-overlay]');
const $ = (sel) => getDrawer()?.querySelector(sel) ?? null;

function getFocusable(container) {
  return Array.from(
    container.querySelectorAll(
      'a[href]:not([hidden]), button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((el) => el.offsetParent !== null || el === document.activeElement);
}

// ─── Open / close ───────────────────────────────────────────────────────────

export function openSearchDrawer(trigger) {
  const drawer = getDrawer();
  if (!drawer || document.body.classList.contains('search-open')) return;

  // Only one right-hand panel at a time. All three are fixed to the same edge
  // and each runs its own focus trap and Esc listener, so two at once means
  // overlapping panels and duelling traps. Cart and nav make the mirror calls.
  window.GrittCart?.close?.();
  window.GrittNav?.closeMobileMenu?.();

  lastTrigger = trigger ?? /** @type {HTMLElement|null} */ (document.activeElement);

  document.body.classList.add('search-open');
  drawer.setAttribute('aria-hidden', 'false');
  lastTrigger?.setAttribute?.('aria-expanded', 'true');

  // Synchronous focus, still inside the click's task: iOS only raises the soft
  // keyboard for a focus() that happens within the user gesture. Safe because
  // the open state sets visibility with no transition delay.
  $('[data-search-input]')?.focus();

  drawer.addEventListener('keydown', trapFocus);
  document.addEventListener('keydown', closeOnEsc);
}

export function closeSearchDrawer() {
  const drawer = getDrawer();
  if (!drawer || !document.body.classList.contains('search-open')) return;

  document.body.classList.remove('search-open');
  drawer.setAttribute('aria-hidden', 'true');
  lastTrigger?.setAttribute?.('aria-expanded', 'false');

  drawer.removeEventListener('keydown', trapFocus);
  document.removeEventListener('keydown', closeOnEsc);

  controller?.abort();
  controller = null;
  clearTimeout(debounceTimer);

  lastTrigger?.focus();
  lastTrigger = null;
}

/** @param {KeyboardEvent} e */
function trapFocus(e) {
  if (e.key !== 'Tab') return;
  const drawer = getDrawer();
  if (!drawer) return;

  const items = getFocusable(drawer);
  if (!items.length) return;

  const first = items[0];
  const last = items[items.length - 1];

  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

/** @param {KeyboardEvent} e */
function closeOnEsc(e) {
  if (e.key === 'Escape') closeSearchDrawer();
}

// ─── Results ────────────────────────────────────────────────────────────────

function setStatus(text) {
  const el = $('[data-search-status]');
  // textContent only — replacing this node breaks the live region.
  if (el) el.textContent = text;
}

function resetResults() {
  const results = $('[data-search-results]');
  const viewAll = $('[data-search-view-all]');
  if (results) {
    results.innerHTML = '';
    results.setAttribute('aria-busy', 'false');
  }
  if (viewAll) viewAll.hidden = true;
  setStatus(getDrawer()?.dataset.initialText || '');
}

function buildUrl(term) {
  const drawer = getDrawer();
  const base = drawer?.dataset.searchEndpoint || '/search/suggest';
  const limit = drawer?.dataset.searchLimit || '8';
  const sectionId = drawer?.dataset.searchSection || 'predictive-search';
  const p = new URLSearchParams({
    q: term,
    'resources[type]': 'product',
    'resources[limit]': limit,
    'resources[options][unavailable_products]': 'last',
    'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor,tag',
    section_id: sectionId,
  });
  return `${base}?${p}`;
}

function render(html, term) {
  const results = $('[data-search-results]');
  const viewAll = $('[data-search-view-all]');
  if (!results) return;

  const fresh = new DOMParser()
    .parseFromString(html, 'text/html')
    .querySelector('[data-predictive-results]');

  if (!fresh) {
    setStatus(getDrawer()?.dataset.errorText || '');
    results.setAttribute('aria-busy', 'false');
    return;
  }

  results.innerHTML = fresh.innerHTML;
  results.setAttribute('aria-busy', 'false');
  setStatus(fresh.dataset.countText || '');

  if (viewAll) {
    const hasResults = fresh.dataset.count !== '0';
    viewAll.hidden = !hasResults;
    if (hasResults) {
      const searchUrl = new URL(viewAll.href, location.origin);
      searchUrl.search = new URLSearchParams({ q: term, type: 'product' }).toString();
      viewAll.href = searchUrl.pathname + searchUrl.search;
    }
  }
}

async function run(term) {
  const results = $('[data-search-results]');
  if (!results) return;

  // Abort BEFORE the early returns, not after. A cache hit or a cooldown that
  // returned while an older fetch was still in flight would otherwise let that
  // fetch land and overwrite the results with a stale term (type "chain", then
  // backspace to a cached "chai").
  controller?.abort();
  controller = null;

  const cached = cache.get(term);
  if (cached !== undefined) {
    render(cached, term);
    return;
  }

  if (Date.now() < cooldownUntil) return;

  controller = new AbortController();
  results.setAttribute('aria-busy', 'true');
  setStatus(getDrawer()?.dataset.searchingText || '');

  try {
    const res = await fetch(buildUrl(term), { signal: controller.signal });
    if (!res.ok) {
      // Shopify throttles predictive search per IP; back off rather than hammer.
      if (res.status === 429) cooldownUntil = Date.now() + ERROR_COOLDOWN_MS;
      throw new Error(`predictive search: ${res.status}`);
    }
    const html = await res.text();
    cache.set(term, html);
    if (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value);
    render(html, term);
  } catch (err) {
    if (err.name === 'AbortError') return;
    results.setAttribute('aria-busy', 'false');
    setStatus(getDrawer()?.dataset.errorText || '');
  }
}

function onInput() {
  const input = $('[data-search-input]');
  if (!input) return;

  const term = input.value.trim();
  const min = parseInt(getDrawer()?.dataset.searchMin || '2', 10);

  clearTimeout(debounceTimer);

  if (term.length < min) {
    controller?.abort();
    controller = null;
    resetResults();
    return;
  }

  debounceTimer = window.setTimeout(() => run(term), DEBOUNCE_MS);
}

// ─── Wiring ─────────────────────────────────────────────────────────────────

function init() {
  // Delegated on document, so the theme editor re-rendering the section (which
  // replaces the drawer node) can never leave a dead listener behind.
  document.addEventListener('click', (e) => {
    const target = /** @type {HTMLElement} */ (e.target);

    const toggle = target.closest('[data-search-toggle]');
    if (toggle) {
      // The trigger is a real link to /search — that is the no-JS fallback.
      e.preventDefault();
      openSearchDrawer(/** @type {HTMLElement} */ (toggle));
      return;
    }

    if (target.closest('[data-search-close]') || target === getOverlay()) {
      closeSearchDrawer();
      return;
    }

    // Following a result closes the panel so it isn't mid-open on bfcache back.
    if (target.closest('[data-search-results] a')) closeSearchDrawer();
  });

  document.addEventListener('input', (e) => {
    if (/** @type {HTMLElement} */ (e.target).closest('[data-search-input]')) onInput();
  });

  window.GrittSearch = { open: openSearchDrawer, close: closeSearchDrawer };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
